// CHAT-SWITCH-A (docs/direction/2026-10-28-chat-slack-switch.md): Slack's search page,
// `/c/{slug}/chat/search`. The box takes words and Slack's typed filters (`from:@Name`
// `in:#channel` `has:file` `is:thread` `before:2026-10-01` `after:2026-09-01`); a submit moves the
// filters it names into the visible controls (From, In, Has file, Threads, After, Before) and
// keeps the words. Everything sits in the URL, so a search is a link. Results are messages the
// caller can read (`search.query`, kind `message`), newest first, "Load more" by cursor; each
// opens its message — the topic with the thread panel on it (`messageLink`, also used by the
// Files tab and reminders).
import {
  Button,
  EmptyState,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { MessageSquareIcon, PaperclipIcon, SearchIcon, XIcon } from "lucide-react";
import { type FormEvent, Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { type FocusSearch, keepFocus } from "../../focus/focus";
import { LoadError, Loading } from "../screen-parts";
import { channelLabel, isDm, type MemberIndex } from "./chat-model";
import { ChatPageBar } from "./chat-pages";
import { channelsQuery, chatKeys, locateQuery, type SidebarChannel } from "./chat-queries";
import { AskButton, AskPanel } from "./chat-switch-b";
import { type ChatContext, useChat } from "./use-chat";

// The message deep link ----------------------------------------------------------------------

export type MessageRef = {
  channel_id: string;
  /** CHAT-TOPICS-SEPARATE: null for a message of the channel's own conversation. */
  topic_id: string | null;
  message_id: string;
  /** A reply's thread root: the thread panel opens on it. */
  parent_message_id?: string | null | undefined;
};

/**
 * Link props that open a message: its topic (the channel, for the channel's own conversation),
 * with the thread panel on the message (a reply's on its root) and the message itself highlighted
 * there (CHAT-SWITCH-B `m`). Spread into `<Link>` or pass to `navigate`.
 */
export function messageLink(chat: Pick<ChatContext, "company">, m: MessageRef) {
  const search = ((prev: Record<string, unknown>) => ({
    ...keepFocus(prev ?? {}),
    thread: m.parent_message_id ?? m.message_id,
    m: m.message_id,
  })) as never;
  if (m.topic_id === null)
    return {
      to: "/c/$slug/chat/$channelId" as "/c/$slug/chat/$channelId/$topicId",
      params: { slug: chat.company.slug, channelId: m.channel_id } as {
        slug: string;
        channelId: string;
        topicId: string;
      },
      search,
    };
  return {
    to: "/c/$slug/chat/$channelId/$topicId" as const,
    params: { slug: chat.company.slug, channelId: m.channel_id, topicId: m.topic_id },
    search,
  };
}

/**
 * `/chat/m/{id}`: asks the API where the message is now (`messages.locate`, under the reader's own
 * access) and opens its conversation on it; says so when it is gone or not the reader's to see.
 */
export function MessageLinkPage({ messageId }: { messageId: string }) {
  const chat = useChat();
  const navigate = useNavigate();
  const loc = useQuery(locateQuery(chat.api, chat.co, messageId));
  const data = loc.data;
  useEffect(() => {
    if (!data?.channel_id || !data.topic_id) return;
    void navigate({
      ...messageLink(chat, {
        channel_id: data.channel_id,
        topic_id: data.topic_id,
        message_id: data.message_id,
        parent_message_id: data.root_id === data.message_id ? null : data.root_id,
      }),
      replace: true,
    });
  }, [data, navigate, chat]);
  if (loc.isError || (data && (!data.channel_id || !data.topic_id)))
    return (
      <div className="p-8">
        <EmptyState
          title="Message not found"
          description="It was deleted, or you can't see where it is."
        />
      </div>
    );
  return <Loading label="Opening the message" />;
}

// The URL ------------------------------------------------------------------------------------

export type ChatSearchParams = {
  q?: string;
  /** A membership id. */
  from?: string;
  /** A channel (or DM) id. */
  in?: string;
  has?: "file";
  is?: "thread";
  before?: string;
  after?: string;
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[A-Za-z0-9_-]{1,64}$/;

const validDate = (d: string) => DATE.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`));

/** The search page's keys of a URL search object (well-formed ones only). */
export function chatSearchParams(s: Record<string, unknown>): ChatSearchParams {
  const out: ChatSearchParams = {};
  if (typeof s.q === "string" && s.q.trim()) out.q = s.q.trim().slice(0, 200);
  if (typeof s.from === "string" && ID.test(s.from)) out.from = s.from;
  if (typeof s.in === "string" && ID.test(s.in)) out.in = s.in;
  if (s.has === "file") out.has = "file";
  if (s.is === "thread") out.is = "thread";
  if (typeof s.before === "string" && validDate(s.before)) out.before = s.before;
  if (typeof s.after === "string" && validDate(s.after)) out.after = s.after;
  return out;
}

const hasFilter = (p: ChatSearchParams) =>
  Boolean(p.from || p.in || p.has || p.is || p.before || p.after);

// Typed filters ------------------------------------------------------------------------------

/** What a typed search says: the words, and each filter as typed (names, not ids yet). */
export type TypedSearch = {
  words: string;
  from?: string;
  in?: string;
  has?: "file";
  is?: "thread";
  before?: string;
  after?: string;
  /** Tokens that are not a filter this page knows (`has:link`, `before:soon`). */
  bad: string[];
};

/** Splits on spaces, keeping `"quoted words"` (after a `key:`, `@` or `#` too) together. */
function tokens(text: string): string[] {
  return text.match(/(?:[^\s"]*"[^"]*"?)+|\S+/g) ?? [];
}

const unquote = (v: string) => v.replace(/"/g, "").trim();

/** Parses Slack's typed filters out of a search box's text. */
export function parseTypedSearch(text: string): TypedSearch {
  const out: TypedSearch = { words: "", bad: [] };
  const words: string[] = [];
  for (const t of tokens(text)) {
    const m = /^(from|in|has|is|before|after|during|on):(.*)$/i.exec(t);
    if (!m) {
      words.push(t);
      continue;
    }
    const key = (m[1] as string).toLowerCase();
    const raw = unquote(m[2] as string);
    if (key === "from" && raw.replace(/^@/, "")) out.from = raw.replace(/^@/, "");
    else if (key === "in" && raw.replace(/^[#@]/, "")) out.in = raw.replace(/^[#@]/, "");
    else if (key === "has" && raw.toLowerCase() === "file") out.has = "file";
    else if (key === "is" && raw.toLowerCase() === "thread") out.is = "thread";
    else if (key === "before" && validDate(raw)) out.before = raw;
    else if (key === "after" && validDate(raw)) out.after = raw;
    else out.bad.push(t);
  }
  out.words = words.join(" ").trim();
  return out;
}

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");

/** The one candidate a typed name means: its whole name, else its first name, else a prefix. */
function pickByName<T>(name: string, items: readonly T[], names: (t: T) => string[]): T | null {
  const want = norm(name);
  if (!want) return null;
  const tiers = [
    (t: T) => names(t).some((n) => norm(n) === want),
    (t: T) => names(t).some((n) => norm(n.split(/\s+/)[0] ?? "") === want),
    (t: T) => names(t).some((n) => norm(n).startsWith(want)),
  ];
  for (const match of tiers) {
    const found = items.filter(match);
    if (found.length === 1) return found[0] as T;
    if (found.length > 1) return null;
  }
  return null;
}

/** `from:@Name`: a person or DE of the company, by name. */
export function resolveMember(name: string, members: MemberIndex): string | null {
  return pickByName(name, [...members.values()], (m) => [m.name])?.id ?? null;
}

/** `in:#name`: a channel the caller can see, by name (a DM by the other person's name). */
export function resolveChannel(
  name: string,
  channels: readonly SidebarChannel[],
  chat: Pick<ChatContext, "meId" | "members">,
): string | null {
  const rooms = channels.filter((c) => !isDm(c));
  const room = pickByName(name, rooms, (c) => [c.name]);
  if (room) return room.id;
  const dms = channels.filter((c) => isDm(c));
  return pickByName(name, dms, (c) => [channelLabel(c, chat)])?.id ?? null;
}

/**
 * A submitted box over the current filters: the typed filters replace the controls' values, the
 * words become `q`. `unknown` lists what could not be used ("from:@Nobody").
 */
export function applyTypedSearch(
  text: string,
  current: ChatSearchParams,
  ctx: {
    members: MemberIndex;
    channels: readonly SidebarChannel[];
    meId: string | null;
  },
): { next: ChatSearchParams; unknown: string[] } {
  const typed = parseTypedSearch(text);
  const next: ChatSearchParams = { ...current };
  delete next.q;
  if (typed.words) next.q = typed.words.slice(0, 200);
  const unknown = [...typed.bad];
  if (typed.from !== undefined) {
    const id = resolveMember(typed.from, ctx.members);
    if (id) next.from = id;
    else unknown.push(`from:@${typed.from}`);
  }
  if (typed.in !== undefined) {
    const id = resolveChannel(typed.in, ctx.channels, ctx);
    if (id) next.in = id;
    else unknown.push(`in:#${typed.in}`);
  }
  if (typed.has) next.has = typed.has;
  if (typed.is) next.is = typed.is;
  if (typed.before) next.before = typed.before;
  if (typed.after) next.after = typed.after;
  return { next, unknown };
}

// Highlight ----------------------------------------------------------------------------------

/** The snippet with each searched word (as a word prefix, any case) marked. */
export function highlight(text: string, q: string | undefined): ReactNode {
  const words = (q ?? "")
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]+/gu, ""))
    .filter(Boolean);
  if (!words.length) return text;
  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(?<![\\p{L}\\p{N}])(${escaped.join("|")})[\\p{L}\\p{N}]*`, "giu");
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (at > last) parts.push(<Fragment key={`t${last}`}>{text.slice(last, at)}</Fragment>);
    parts.push(
      <mark key={`m${at}`} className="rounded-sm bg-primary-soft px-0.5 text-foreground">
        {m[0]}
      </mark>,
    );
    last = at + m[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={`t${last}`}>{text.slice(last)}</Fragment>);
  return parts;
}

// The page -----------------------------------------------------------------------------------

type Hit = Awaited<ReturnType<ChatContext["api"]["client"]["search"]["query"]>>["data"][number];

export const chatSearchKey = (co: string, p: ChatSearchParams) =>
  [...chatKeys.root(co), "search", p] as const;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export function ChatSearchPage() {
  const chat = useChat();
  const navigate = useNavigate();
  const raw = useSearch({ strict: false }) as Record<string, unknown>;
  const params = useMemo(() => chatSearchParams(raw), [raw]);
  const channels = useQuery(channelsQuery(chat.api, chat.co));
  const allChannels = channels.data?.data ?? [];
  const [text, setText] = useState(params.q ?? "");
  const [unknown, setUnknown] = useState<string[]>([]);
  const [askId, setAskId] = useState<string | null>(null);
  // Back/forward (or a link) changes the words: the box follows.
  useEffect(() => setText(params.q ?? ""), [params.q]);

  const go = (next: ChatSearchParams) =>
    void navigate({
      to: "/c/$slug/chat/search",
      params: { slug: chat.company.slug },
      search: ((prev: Record<string, unknown>) => ({ ...keepFocus(prev ?? {}), ...next })) as never,
    });
  const set = (patch: Partial<Record<keyof ChatSearchParams, string | undefined>>) => {
    const next = { ...params, ...patch } as Record<string, string | undefined>;
    for (const k of Object.keys(next)) if (!next[k]) delete next[k];
    go(next as ChatSearchParams);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = applyTypedSearch(text, params, {
      members: chat.members,
      channels: allChannels,
      meId: chat.meId,
    });
    setUnknown(r.unknown);
    setText(r.next.q ?? "");
    go(r.next);
  };

  const active = Boolean(params.q) || hasFilter(params);
  const results = useInfiniteQuery({
    queryKey: chatSearchKey(chat.co, params),
    queryFn: ({ pageParam }) =>
      chat.api.client.search.query({
        params: { co: chat.co },
        query: {
          ...params,
          kind: "message",
          limit: 30,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: active,
    placeholderData: keepPreviousData,
  });
  const hits = results.data?.pages.flatMap((p) => p.data) ?? [];
  const byChannel = new Map(allChannels.map((c) => [c.id, c]));

  const people = [...chat.members.values()].sort((a, b) => a.name.localeCompare(b.name));
  const rooms = allChannels
    .map((c) => ({ id: c.id, label: channelLabel(c, chat), dm: isDm(c) }))
    .sort((a, b) => Number(a.dm) - Number(b.dm) || a.label.localeCompare(b.label));

  return (
    <div data-page="chat-search" className="flex h-full min-h-0 flex-col">
      <ChatPageBar title="Search">
        <InfoTip label="How to search">
          Type words, or filters: <code>from:@Name</code> <code>in:#channel</code>{" "}
          <code>has:file</code> <code>is:thread</code> <code>before:2026-10-01</code>{" "}
          <code>after:2026-09-01</code>. Before and after leave out the day itself.
        </InfoTip>
      </ChatPageBar>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3 max-md:px-3">
        <form aria-label="Search" onSubmit={submit} className="flex max-w-3xl items-center gap-2">
          <Input
            aria-label="Search messages"
            placeholder="Search messages (from:@Name in:#channel has:file …)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="flex-1"
          />
          <Button type="submit">
            <SearchIcon data-icon="inline-start" />
            Search
          </Button>
          {/* CHAT-SWITCH-B: ask a DE about what this search finds. */}
          {active ? <AskButton chat={chat} params={params} onAsked={setAskId} /> : null}
        </form>
        {askId ? <AskPanel chat={chat} askId={askId} onClose={() => setAskId(null)} /> : null}
        <div
          data-slot="search-filters"
          className="mt-2 flex max-w-3xl flex-wrap items-center gap-1.5"
        >
          <NativeSelect
            size="sm"
            aria-label="From"
            value={params.from ?? ""}
            onChange={(e) => set({ from: e.target.value || undefined })}
          >
            <NativeSelectOption value="">From: anyone</NativeSelectOption>
            {people.map((m) => (
              <NativeSelectOption key={m.id} value={m.id}>
                {m.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            size="sm"
            aria-label="In"
            value={params.in ?? ""}
            onChange={(e) => set({ in: e.target.value || undefined })}
          >
            <NativeSelectOption value="">In: anywhere</NativeSelectOption>
            {rooms.map((c) => (
              <NativeSelectOption key={c.id} value={c.id}>
                {c.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <Button
            size="sm"
            variant={params.has ? "secondary" : "outline"}
            aria-pressed={Boolean(params.has)}
            onClick={() => set({ has: params.has ? undefined : "file" })}
          >
            <PaperclipIcon data-icon="inline-start" />
            Has file
          </Button>
          <Button
            size="sm"
            variant={params.is ? "secondary" : "outline"}
            aria-pressed={Boolean(params.is)}
            onClick={() => set({ is: params.is ? undefined : "thread" })}
          >
            <MessageSquareIcon data-icon="inline-start" />
            Threads
          </Button>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <span aria-hidden="true">After</span>
            <Input
              type="date"
              aria-label="After"
              value={params.after ?? ""}
              onChange={(e) => set({ after: e.target.value || undefined })}
              className="h-8 w-36"
            />
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <span aria-hidden="true">Before</span>
            <Input
              type="date"
              aria-label="Before"
              value={params.before ?? ""}
              onChange={(e) => set({ before: e.target.value || undefined })}
              className="h-8 w-36"
            />
          </span>
          {hasFilter(params) ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setUnknown([]);
                go(params.q ? { q: params.q } : {});
              }}
            >
              <XIcon data-icon="inline-start" />
              Clear filters
            </Button>
          ) : null}
        </div>
        {unknown.length ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            Not found: {unknown.join(" ")}
          </p>
        ) : null}
        <div className="mt-3 max-w-3xl">
          {!active ? (
            <EmptyState
              variant="compact"
              icon={SearchIcon}
              title="Search messages"
              description="Words, a person, a channel or a date."
            />
          ) : results.isError ? (
            <LoadError what="Search" error={results.error} />
          ) : results.isPending ? (
            <Loading label="Loading results" />
          ) : !hits.length ? (
            <EmptyState variant="compact" icon={SearchIcon} title="No messages found" />
          ) : (
            <ul aria-label="Results" className="m-0 flex list-none flex-col p-0">
              {hits.map((h) => (
                <ResultRow
                  key={h.id}
                  chat={chat}
                  hit={h}
                  q={params.q}
                  channel={h.channel_id ? byChannel.get(h.channel_id) : undefined}
                />
              ))}
            </ul>
          )}
          {results.hasNextPage ? (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={results.isFetchingNextPage}
              onClick={() => void results.fetchNextPage()}
            >
              {results.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ResultRow({
  chat,
  hit,
  q,
  channel,
}: {
  chat: ChatContext;
  hit: Hit;
  q: string | undefined;
  channel: SidebarChannel | undefined;
}) {
  // A DM by the other person's name (the hit's title is its stored name).
  const place = channel && isDm(channel) ? channelLabel(channel, chat) : hit.title;
  const author = hit.author_membership_id
    ? (chat.members.get(hit.author_membership_id)?.name ?? "Someone")
    : "CompanyOS";
  const body = (
    <>
      <span className="flex min-w-0 items-baseline gap-2 text-xs text-muted-foreground">
        <span className="truncate font-medium text-secondary">{place}</span>
        {hit.parent_message_id ? <span className="shrink-0">in a thread</span> : null}
      </span>
      <span className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-foreground">{author}</span>
        <time dateTime={hit.created_at} className="text-xs text-muted-foreground tabular-nums">
          {when(hit.created_at)}
        </time>
      </span>
      <span data-slot="search-snippet" className="line-clamp-3 text-sm text-foreground">
        {highlight(hit.snippet, q)}
      </span>
    </>
  );
  const cls =
    "flex flex-col gap-0.5 rounded-md px-3 py-2 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring";
  return (
    <li data-slot="search-result" className="border-b border-border-subtle last:border-b-0">
      {hit.channel_id ? (
        <Link
          className={cls}
          {...messageLink(chat, {
            channel_id: hit.channel_id,
            topic_id: hit.topic_id,
            message_id: hit.id,
            parent_message_id: hit.parent_message_id ?? null,
          })}
        >
          {body}
        </Link>
      ) : (
        <div className={cls}>{body}</div>
      )}
    </li>
  );
}

/** For other screens' links: the search page with these keys (focus kept). */
export const searchPageSearch = (p: ChatSearchParams) =>
  ((prev: Record<string, unknown>) => ({ ...keepFocus(prev ?? {}), ...p })) as unknown as (
    prev: Record<string, unknown>,
  ) => FocusSearch & ChatSearchParams;
